import { useState, useEffect, Component } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { Button } from '@/components/ui/button';
import {
  LayoutDashboard, FileText, Image, Scale,
  Settings, Search, Users, QrCode, Info, LogOut, Menu, ChevronRight, HardDrive, Layers,
  AlertCircle, RefreshCw
} from 'lucide-react';

const navItems = [
  { path: '/admin/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { path: '/admin/content', label: 'Inhalte', icon: FileText },
  { path: '/admin/hero-combinations', label: 'Hero-Kombinationen', icon: Layers },
  { path: '/admin/media', label: 'Medien', icon: Image },
  { path: '/admin/legal', label: 'Rechtstexte', icon: Scale },
  { path: '/admin/settings', label: 'Einstellungen', icon: Settings },
  { path: '/admin/seo', label: 'SEO', icon: Search },
  { path: '/admin/users', label: 'Benutzer', icon: Users },
  { path: '/admin/qrcode', label: 'QR-Code', icon: QrCode },
  { path: '/admin/backup', label: 'Backup', icon: HardDrive },
  { path: '/admin/version', label: 'Version', icon: Info },
];

// Error Boundary direkt eingebettet (kein externer Import)
class PageErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  componentDidCatch(error, info) {
    console.error('Admin page error:', error, info);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="flex items-center justify-center min-h-[400px] p-8" data-testid="admin-error-boundary">
          <div className="text-center max-w-md">
            <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
            <h2 className="text-lg font-semibold text-slate-900 font-body mb-2">
              Seite konnte nicht geladen werden
            </h2>
            <p className="text-sm text-slate-500 font-body mb-6">
              Ein Fehler ist aufgetreten. Bitte laden Sie die Seite neu.
            </p>
            <Button
              onClick={() => { this.setState({ hasError: false }); window.location.reload(); }}
              className="bg-[#0E518D] hover:bg-[#0B4171] text-white font-body"
              data-testid="admin-error-retry"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Seite neu laden
            </Button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

// Ladebildschirm
function LoadingScreen() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50" data-testid="admin-loading">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0E518D]" />
    </div>
  );
}

export default function AdminLayout() {
  const { user, loading, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Redirect zu Login nur wenn: Auth-Check abgeschlossen UND kein User UND kein Token
  useEffect(() => {
    if (loading) return;
    if (user) return;
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/admin/login', { replace: true });
    }
  }, [user, loading, navigate]);

  // Sidebar bei Navigation schliessen
  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  // Phase 1: Auth-System laedt noch
  if (loading) {
    return <LoadingScreen />;
  }

  // Phase 2: Token vorhanden aber User-State noch nicht gesetzt (Race Condition nach Login)
  if (!user) {
    const token = localStorage.getItem('token');
    if (token) {
      return <LoadingScreen />;
    }
    // Phase 3: Kein Token, kein User - Redirect laeuft via useEffect
    return <LoadingScreen />;
  }

  // Phase 4: User authentifiziert - Admin-Bereich rendern
  const currentPage = navItems.find(item => item.path === location.pathname);

  return (
    <div className="min-h-screen bg-slate-50 flex" data-testid="admin-layout">
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/30 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      <aside
        className={`fixed lg:static inset-y-0 left-0 z-50 w-64 bg-white border-r border-slate-200 transform transition-transform duration-200 lg:transform-none ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        }`}
        data-testid="admin-sidebar"
      >
        <div className="flex flex-col h-full">
          <div className="p-4 border-b border-slate-100">
            <Link to="/admin/dashboard" className="flex items-center gap-2">
              <div className="w-8 h-8 bg-[#0E518D] rounded-sm flex items-center justify-center">
                <Settings className="w-4 h-4 text-white" strokeWidth={1.5} />
              </div>
              <div>
                <p className="text-sm font-semibold text-slate-900 font-body">Admin</p>
                <p className="text-xs text-slate-500 font-body">GB Sylt Mietwagen</p>
              </div>
            </Link>
          </div>

          <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  data-testid={`admin-nav-${item.path.split('/').pop()}`}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm font-body transition-all duration-200 ${
                    isActive
                      ? 'bg-[#0E518D] text-white shadow-sm'
                      : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                  }`}
                >
                  <Icon className="w-4 h-4" strokeWidth={1.5} />
                  {item.label}
                </Link>
              );
            })}
          </nav>

          <div className="p-3 border-t border-slate-100">
            <div className="px-3 py-2 text-xs text-slate-500 font-body mb-2">
              {user.username} ({user.role})
            </div>
            <Button
              variant="ghost"
              className="w-full justify-start gap-3 text-sm text-slate-600 hover:text-red-600 hover:bg-red-50 font-body"
              onClick={() => { logout(); navigate('/admin/login'); }}
              data-testid="admin-logout-button"
            >
              <LogOut className="w-4 h-4" strokeWidth={1.5} />
              Abmelden
            </Button>
          </div>
        </div>
      </aside>

      <div className="flex-1 flex flex-col min-h-screen">
        <header className="bg-white border-b border-slate-200 px-4 lg:px-6 py-3 flex items-center gap-4">
          <button
            className="lg:hidden p-1.5 rounded-sm hover:bg-slate-100"
            onClick={() => setSidebarOpen(true)}
            data-testid="admin-menu-toggle"
          >
            <Menu className="w-5 h-5 text-slate-600" />
          </button>
          <div className="flex items-center gap-2 text-sm font-body text-slate-500">
            <span>Admin</span>
            {currentPage && (
              <>
                <ChevronRight className="w-3 h-3" />
                <span className="text-slate-900 font-medium">{currentPage.label}</span>
              </>
            )}
          </div>
        </header>

        <main className="flex-1 p-4 lg:p-6">
          <PageErrorBoundary key={location.pathname}>
            <Outlet />
          </PageErrorBoundary>
        </main>
      </div>
    </div>
  );
}
